import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ─────────────────────────────────────────────
   Types
   ───────────────────────────────────────────── */
type ModalField = {
  id: string;
  label: string;
  placeholder?: string;
  type?: 'text' | 'url' | 'toggle';
  required?: boolean;
  defaultValue?: string;
};

interface CmsModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  /** Optional subtitle / description below the title */
  description?: string;
  /** Visual variant */
  variant?: 'default' | 'danger' | 'warning' | 'success';
  /** Icon class (e.g. "bi bi-trash") displayed beside the title */
  icon?: string;

  /* ── Confirm mode (no fields) ── */
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;

  /* ── Prompt mode (with fields) ── */
  fields?: ModalField[];
  /** Called with { [fieldId]: value } */
  onSubmit?: (values: Record<string, string>) => void;
  submitText?: string;

  /* ── Alert mode (single button) ── */
  isAlert?: boolean;
}

/* ─────────────────────────────────────────────
   Styles
   ───────────────────────────────────────────── */
const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0,0,0,0.35)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  animation: 'cmsModalFadeIn 0.2s ease',
};

const CARD_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: '440px',
  borderRadius: '16px',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  overflow: 'hidden',
  animation: 'cmsModalSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
};

const VARIANT_COLORS: Record<string, { accent: string; bg: string; btnClass: string }> = {
  default: { accent: '#3E97FF', bg: '#EEF6FF', btnClass: 'btn-primary' },
  danger:  { accent: '#F1416C', bg: '#FFF5F8', btnClass: 'btn-danger' },
  warning: { accent: '#FFC700', bg: '#FFF8DD', btnClass: 'btn-warning' },
  success: { accent: '#50CD89', bg: '#E8FFF3', btnClass: 'btn-success' },
};

/* ─────────────────────────────────────────────
   Keyframe injection (runs once)
   ───────────────────────────────────────────── */
let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
    @keyframes cmsModalFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes cmsModalSlideUp { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────
   Component
   ───────────────────────────────────────────── */
export const CmsModal: React.FC<CmsModalProps> = ({
  show,
  onClose,
  title,
  description,
  variant = 'default',
  icon,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  fields,
  onSubmit,
  submitText = 'Submit',
  isAlert = false,
}) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const firstInputRef = useRef<HTMLInputElement>(null);
  const colors = VARIANT_COLORS[variant] || VARIANT_COLORS.default;

  useEffect(() => { injectStyles(); }, []);

  // Reset field values when modal opens
  useEffect(() => {
    if (show && fields) {
      const initial: Record<string, string> = {};
      fields.forEach(f => { initial[f.id] = f.defaultValue || ''; });
      setFieldValues(initial);
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [show, fields]);

  // Escape key to close
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [show, handleKeyDown]);

  if (!show) return null;

  const hasFields = fields && fields.length > 0;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasFields && onSubmit) {
      // Check required fields
      const allFilled = fields!.every(f => !f.required || fieldValues[f.id]?.trim());
      if (!allFilled) return;
      onSubmit(fieldValues);
    }
  };

  const handleConfirmClick = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  return (
    <div style={OVERLAY_STYLE} onClick={onClose}>
      <div style={CARD_STYLE} className="bg-white" onClick={(e) => e.stopPropagation()}>
        {/* Header strip */}
        <div style={{ height: '4px', backgroundColor: colors.accent }} />

        <div className="p-7">
          {/* Icon + Title */}
          <div className="d-flex align-items-start gap-3 mb-1">
            {icon && (
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{ width: '40px', height: '40px', backgroundColor: colors.bg }}
              >
                <i className={icon} style={{ color: colors.accent, fontSize: '18px' }}></i>
              </div>
            )}
            <div className="d-flex flex-column">
              <h4 className="fw-bolder text-dark mb-1 fs-4">{title}</h4>
              {description && <p className="text-muted fs-7 mb-0">{description}</p>}
            </div>
          </div>

          {/* Fields (prompt mode) */}
          {hasFields && (
            <form onSubmit={handleFormSubmit} className="mt-5">
              <div className="d-flex flex-column gap-4">
                {fields!.map((field, idx) => (
                  <div key={field.id} className={field.type === 'toggle' ? 'd-flex align-items-center justify-content-between' : 'd-flex flex-column gap-1'}>
                    <label className={`fw-bold fs-7 text-gray-700${field.type === 'toggle' ? ' mb-0' : ''}`}>{field.label}</label>
                    {field.type === 'toggle' ? (
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={fieldValues[field.id] === 'true'}
                          onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.checked ? 'true' : 'false' }))}
                          style={{ width: '44px', height: '22px', cursor: 'pointer' }}
                        />
                      </div>
                    ) : (
                      <input
                        ref={idx === 0 ? firstInputRef : undefined}
                        type={field.type || 'text'}
                        className="form-control form-control-solid"
                        placeholder={field.placeholder || ''}
                        value={fieldValues[field.id] || ''}
                        onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-end gap-3 mt-6">
                <button type="button" className="btn btn-light fw-bold" onClick={onClose}>{cancelText}</button>
                <button type="submit" className={`btn ${colors.btnClass} fw-bold`}>{submitText}</button>
              </div>
            </form>
          )}

          {/* Confirm mode (no fields) */}
          {!hasFields && !isAlert && (
            <div className="d-flex justify-content-end gap-3 mt-6">
              <button className="btn btn-light fw-bold" onClick={onClose}>{cancelText}</button>
              <button className={`btn ${colors.btnClass} fw-bold`} onClick={handleConfirmClick}>{confirmText}</button>
            </div>
          )}

          {/* Alert mode (single button) */}
          {isAlert && (
            <div className="d-flex justify-content-end mt-6">
              <button className={`btn ${colors.btnClass} fw-bold`} onClick={onClose}>OK</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Imperative helpers (drop-in replacements)
   ───────────────────────────────────────────── */

/** Container for portal-rendering modals */
let _container: HTMLDivElement | null = null;
function getContainer() {
  if (!_container) {
    _container = document.createElement('div');
    _container.id = 'cms-modal-root';
    document.body.appendChild(_container);
  }
  return _container;
}

/**
 * Drop-in replacement for window.confirm().
 * Returns a Promise<boolean>.
 */
export function cmsConfirm(opts: {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  icon?: string;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const container = getContainer();
    const root = document.createElement('div');
    container.appendChild(root);

    const cleanup = () => {
      const reactRoot = (window as any).__reactDomClient;
      // Simple DOM cleanup
      root.remove();
    };

    const { createRoot } = require('react-dom/client');
    const reactRoot = createRoot(root);
    
    reactRoot.render(
      <CmsModal
        show={true}
        title={opts.title}
        description={opts.description}
        variant={opts.variant || 'default'}
        icon={opts.icon}
        confirmText={opts.confirmText || 'Confirm'}
        cancelText={opts.cancelText || 'Cancel'}
        onConfirm={() => {
          resolve(true);
          reactRoot.unmount();
          cleanup();
        }}
        onClose={() => {
          resolve(false);
          reactRoot.unmount();
          cleanup();
        }}
      />
    );
  });
}

/**
 * Drop-in replacement for window.alert().
 * Returns a Promise<void>.
 */
export function cmsAlert(opts: {
  title: string;
  description?: string;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  icon?: string;
}): Promise<void> {
  return new Promise((resolve) => {
    const container = getContainer();
    const root = document.createElement('div');
    container.appendChild(root);

    const cleanup = () => { root.remove(); };

    const { createRoot } = require('react-dom/client');
    const reactRoot = createRoot(root);
    
    reactRoot.render(
      <CmsModal
        show={true}
        title={opts.title}
        description={opts.description}
        variant={opts.variant || 'default'}
        icon={opts.icon}
        isAlert={true}
        onClose={() => {
          resolve();
          reactRoot.unmount();
          cleanup();
        }}
      />
    );
  });
}

/**
 * Drop-in replacement for window.prompt() with multiple fields.
 * Returns a Promise<Record<string, string> | null>.
 */
export function cmsPrompt(opts: {
  title: string;
  description?: string;
  fields: ModalField[];
  submitText?: string;
  cancelText?: string;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  icon?: string;
}): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    const container = getContainer();
    const root = document.createElement('div');
    container.appendChild(root);

    const cleanup = () => { root.remove(); };

    const { createRoot } = require('react-dom/client');
    const reactRoot = createRoot(root);
    
    reactRoot.render(
      <CmsModal
        show={true}
        title={opts.title}
        description={opts.description}
        variant={opts.variant || 'default'}
        icon={opts.icon}
        fields={opts.fields}
        submitText={opts.submitText || 'Add'}
        cancelText={opts.cancelText || 'Cancel'}
        onSubmit={(values) => {
          resolve(values);
          reactRoot.unmount();
          cleanup();
        }}
        onClose={() => {
          resolve(null);
          reactRoot.unmount();
          cleanup();
        }}
      />
    );
  });
}

export default CmsModal;
